import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api, ApiError, streamDocument } from '../../lib/api'
import { STATUS_PILL, ago, money } from '../../lib/format'
import type { DocumentSummary } from '../../lib/types'
import { useAuth } from '../../app/AuthContext'
import { useToast } from '../../app/Toasts'
import CameraCapture, { cameraSupported } from './CameraCapture'

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp'
const FILTERS = [
  { value: '', label: 'All' },
  { value: 'needs_review', label: 'Needs review' },
  { value: 'approved', label: 'Approved' },
  { value: 'failed', label: 'Failed' },
]

interface Progress { step: string; status: string }

export default function DocumentsPage() {
  const navigate = useNavigate()
  const { push } = useToast()
  const { can } = useAuth()

  const [docs, setDocs] = useState<DocumentSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('')
  const [over, setOver] = useState(false)
  const [progress, setProgress] = useState<Record<string, Progress>>({})
  const [camera, setCamera] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // Fallback path: over plain http, or in a browser without getUserMedia, this
  // hands off to the phone's own camera app instead of showing nothing.
  const captureRef = useRef<HTMLInputElement>(null)
  const streams = useRef<Map<string, () => void>>(new Map())

  const load = useCallback(async () => {
    try {
      const { items } = await api.listDocuments(filter ? { status: filter } : {})
      setDocs(items)
    } catch (e) {
      if (e instanceof ApiError && e.status !== 401) {
        push('error', 'Could not load documents', e.message)
      }
    } finally {
      setLoading(false)
    }
  }, [filter, push])

  useEffect(() => { load() }, [load])

  // Safety net only. SSE carries the fast path; this covers a dropped stream,
  // which Cloudflare Tunnel does occasionally cause. Keyed on whether anything
  // is running rather than on `docs`, so the interval is not torn down and
  // recreated on every list refresh.
  const anyRunning = docs.some((d) => d.status === 'extracting' || d.status === 'received')
  useEffect(() => {
    if (!anyRunning) return
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load()
    }, 8000)
    return () => window.clearInterval(timer)
  }, [anyRunning, load])

  useEffect(() => {
    const running = docs.filter((d) => d.status === 'extracting' || d.status === 'received')
    for (const doc of running) {
      if (streams.current.has(doc.id)) continue
      const stop = streamDocument(doc.id, (_event, payload) => {
        setProgress((current) => ({
          ...current,
          [doc.id]: {
            step: String(payload.step ?? ''),
            status: String(payload.status ?? ''),
          },
        }))
        if (['needs_review', 'approved', 'failed'].includes(String(payload.status))) {
          streams.current.get(doc.id)?.()
          streams.current.delete(doc.id)
          load()
        }
      })
      streams.current.set(doc.id, stop)
    }
    // Drop streams for documents that finished or left the filter.
    for (const [docId, stop] of streams.current) {
      if (!running.some((d) => d.id === docId)) { stop(); streams.current.delete(docId) }
    }
  }, [docs, load])

  useEffect(() => () => {
    for (const stop of streams.current.values()) stop()
    streams.current.clear()
  }, [])

  const send = useCallback(async (files: FileList | File[]) => {
    const list = Array.from(files)
    if (list.length === 0) return
    for (const file of list) {
      try {
        const result = await api.upload(file)
        if (result.duplicate) {
          push('info', 'Already uploaded', result.message)
        } else {
          push('success', `Reading ${file.name}`, 'Extraction takes about 30 seconds.')
        }
      } catch (e) {
        push('error', `Could not upload ${file.name}`,
          e instanceof ApiError ? e.message : undefined)
      }
    }
    load()
  }, [load, push])

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="flow">
        {[
          ['1', 'Upload', 'PDF or photo of any supplier invoice'],
          ['2', 'We read it', 'Fields extracted and checked against GST rules'],
          ['3', 'You approve', 'Fix anything flagged, then it is stored'],
        ].map(([n, title, hint]) => (
          <div className="flow-step" key={n}>
            <span className="flow-num">{n}</span>
            <div>
              <div className="flow-title">{title}</div>
              <div className="flow-hint">{hint}</div>
            </div>
          </div>
        ))}
      </div>

      {can('document:write') && (
        <div
          className={`dropzone${over ? ' over' : ''}`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setOver(true) }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault()
            setOver(false)
            send(e.dataTransfer.files)
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click() }}
        >
          <div className="dropzone-title">Drop invoices here</div>
          <div className="dropzone-hint">
            PDF, PNG, JPG or WEBP · up to 15 MB and 20 pages each
          </div>

          {/* Photographing a paper invoice is the whole point on a phone, so it
              gets its own button rather than hiding inside the file picker.
              stopPropagation: the dropzone itself opens the file picker. */}
          <div className="dropzone-actions" onClick={(e) => e.stopPropagation()}>
            <button className="btn btn-sm" onClick={() => inputRef.current?.click()}>
              Choose files
            </button>
            <button className="btn btn-sm btn-primary"
              onClick={() => (cameraSupported() ? setCamera(true) : captureRef.current?.click())}>
              Take photo
            </button>
          </div>

          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            hidden
            onChange={(e) => { if (e.target.files) send(e.target.files); e.target.value = '' }}
          />
          <input
            ref={captureRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => { if (e.target.files) send(e.target.files); e.target.value = '' }}
          />
        </div>
      )}

      {camera && (
        <CameraCapture
          onClose={() => setCamera(false)}
          onCapture={(file) => send([file])}
        />
      )}

      <div className="card">
        <div className="card-head">
          <span className="card-title">Queue</span>
          <div className="grow" />
          <div className="wrap-gap">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                className={`btn btn-sm${filter === f.value ? ' btn-primary' : ''}`}
                onClick={() => setFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="card-body" style={{ display: 'grid', gap: 8 }}>
            {[0, 1, 2].map((i) => <div key={i} className="skeleton" style={{ height: 38 }} />)}
          </div>
        ) : docs.length === 0 ? (
          <div className="empty">
            <div className="empty-title">Nothing here yet</div>
            <div>Upload an invoice to get started.</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Vendor</th>
                  <th>Invoice</th>
                  <th className="num">Total</th>
                  <th>Status</th>
                  <th>Issues</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {docs.map((doc) => {
                  const running = doc.status === 'extracting' || doc.status === 'received'
                  const pill = STATUS_PILL[doc.status] ?? STATUS_PILL.received
                  const step = progress[doc.id]?.step
                  return (
                    <tr
                      key={doc.id}
                      className="clickable"
                      onClick={() => !running && navigate(`/inbox/${doc.id}/review`)}
                    >
                      <td>
                        <div style={{ fontWeight: 520 }}>{doc.filename}</div>
                        <div className="muted" style={{ fontSize: 11.5 }}>
                          {ago(doc.created_at)}
                          {doc.page_count > 1 && ` · ${doc.page_count} pages`}
                        </div>
                      </td>
                      <td>{doc.vendor?.name ?? <span className="muted">—</span>}</td>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {doc.invoice_number ?? <span className="muted">—</span>}
                      </td>
                      <td className="num">
                        {doc.grand_total
                          ? money(doc.grand_total, doc.currency ?? 'INR')
                          : <span className="muted">—</span>}
                      </td>
                      <td>
                        <span className={pill.className}>
                          {running && <span className="spinner" style={{ width: 9, height: 9 }} />}
                          {pill.text}
                        </span>
                        {running && step && (
                          <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
                            {step}
                          </div>
                        )}
                        {doc.status === 'failed' && doc.error && (
                          <div
                            className="muted"
                            style={{ fontSize: 11, marginTop: 3, maxWidth: 260 }}
                          >
                            {doc.error.slice(0, 90)}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="row" style={{ gap: 5 }}>
                          {doc.errors > 0 && (
                            <span className="pill pill-crit">{doc.errors}</span>
                          )}
                          {doc.warnings > 0 && (
                            <span className="pill pill-warn">{doc.warnings}</span>
                          )}
                          {doc.errors === 0 && doc.warnings === 0 && !running && (
                            <span className="pill pill-ok">clean</span>
                          )}
                        </div>
                      </td>
                      <td className="num">
                        {!running && (
                          <button
                            className="btn btn-sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/inbox/${doc.id}/review`)
                            }}
                          >
                            {doc.status === 'approved' ? 'View' : 'Review'}
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
